<template>
	<div class="flex justify-between items-center w-full px-12 border-box cursor-pointer"
		 :style="{height: props.heightStyle, backgroundColor: props.selected && props.color ? props.color : null}"
		 :class="[{'hover:bg-gray-150 text-text-color': !props.selected, 'bg-gray-150': !props.selected && props.focused, 'text-white': props.selected, 'bg-success': props.selected && !props.color, 'opacity-50': props.disabled}]"
		 @click.stop="toggleOption">
		<div v-if="props.multiple" class="mr-16">
			<Icon :icon="props.selected ? icons.checked : icons.unchecked"
				  :color="props.selected ? 'white': 'gray-400'"/>
		</div>
		<div class="flex items-center flex-grow-x-1 overflow-hidden">
			<lazy-avatar v-if="props.image" :src="props.image" :size="props.imageSize"
						 class="mr-16"
						 :ratio="(props.image?.ratio ?? props.imageRatio)"
						 contain
						 :round="!props.imageTile && (props.image?.ratio ?? props.imageRatio) === 1"
						 :tile="props.imageTile"/>
			<Icon v-else-if="props.icon" :icon="props.icon"
				  small
				  :color="props.selected ? 'white': 'gray-400'"
				  class="mr-16"/>
			<span class="text-sub leading-tight truncate" :title="props.text">{{ props.text }}</span>
		</div>
		<div v-if="props.help"
			  class="text-right text-uppercase text-xs truncate max-w-half min-w-1/5"
			  :class="props.selected ? 'text-gray-100' : 'text-gray-400'">{{ props.help }}</div>
	</div>
</template>

<script setup lang="ts">
import type {Image} from "@spoferan/spoferan-ts-core";
import {mdiCheckboxBlankOutline, mdiCheck} from "@mdi/js";

const props = withDefaults(defineProps<{
	heightStyle?: string,

	// The color to style the option when selected
	color?: string,

	// Whether the option is actively selected
	selected?: boolean,

	// Whether multiple options can be selected
	multiple?: boolean,

	// Whether the option is disabled and cannot be selected
	disabled?: boolean,

	// Whether the option is focused, e.g., via keyboard navigation
	focused?: boolean,

	// The main option text to display
	text?: string | number,

	// An image to display on the left side of the option as option prefix
	image?: Image | string,

	// The size of the image prefix to display
	imageSize?: number,

	// A custom image ratio to use for the image when none is specified via image.ratio
	imageRatio?: number,

	// Whether the image should be displayed as a tile
	imageTile?: boolean,

	// An icon to display on the left side of the option as option prefix
	icon?: string,

	// A short help text to display on the right side of the option as option suffix
	help?: string,
}>(), {
	imageRatio: 1,
	imageSize: 20
});

const emit = defineEmits(['toggle']);

const icons = {
	unchecked: mdiCheckboxBlankOutline,
	checked: mdiCheck
}

function toggleOption() {
	if (!props.disabled) {
		emit('toggle');
	}
}
</script>